import { Suspense, type CSSProperties, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Error } from "@/components/admin/error";
import { Notification } from "@/components/admin/notification";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router";
import { DataImportProvider } from "@/components/atomic-crm/dataImport/DataImportProvider";
import { useConfigurationLoader } from "@/components/atomic-crm/root/useConfigurationLoader";
import { PullToRefresh } from "@/components/atomic-crm/layout/PullToRefresh";
import { DialerApiRoot } from "../dialer/DialerApiRoot";
import { useInboundReturn } from "../dialer/inbound/useInboundReturn"; // GAVION inbound calls
// GAVION batch 3: M365Api
import { M365ApiRoot } from "../integrations/M365ApiRoot";
import { AiApiRoot } from "../ai/AiApiRoot"; // GAVION ai-email
import { GavionSidebar } from "./GavionSidebar";
import { GavionTopBar } from "./GavionTopBar";
import { MobileTabBar } from "./MobileTabBar";
import { MAIN_WIDTH_CLASS, pageWidthFor } from "./pageWidth";

const Content = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary FallbackComponent={Error}>
    <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

const SIDEBAR_SIZES = {
  "--sidebar-width": "15rem",
  "--sidebar-width-icon": "4rem",
} as CSSProperties;

/** One DialerApi (DialerApiRoot) wraps every screen, the top bar and the session pill. Spec 10 §4: dark sidebar (240 px, 64 px icons) from 768 px, expanded by default from 1024 px; bottom tabs below 768 px. */
export const GavionLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  useInboundReturn(); // GAVION inbound calls: the « Appel entrant » link survives the sign-in
  const isMobile = useIsMobile();
  const width = pageWidthFor(useLocation().pathname);
  if (isMobile) {
    return (
      <DataImportProvider>
        <DialerApiRoot>
          <M365ApiRoot>
            <AiApiRoot>
              <PullToRefresh />
              {/* room for the fixed tab bar, including the iPhone home-indicator inset it pads itself with */}
              <div className="pb-[calc(5rem+env(safe-area-inset-bottom))]">
                <Content>{children}</Content>
              </div>
              <MobileTabBar />
              <Notification mobileOffset={{ bottom: "72px" }} />
            </AiApiRoot>
          </M365ApiRoot>
        </DialerApiRoot>
      </DataImportProvider>
    );
  }
  const defaultOpen =
    typeof window === "undefined" || window.innerWidth >= 1024;
  return (
    <DataImportProvider>
      <DialerApiRoot>
        <M365ApiRoot>
          <AiApiRoot>
            <SidebarProvider defaultOpen={defaultOpen} style={SIDEBAR_SIZES}>
              <GavionSidebar />
              {/* min-w-0: wide content (deal board, leads table) scrolls inside its own container instead of widening the page. */}
              <SidebarInset className="min-w-0">
                <GavionTopBar />
                {/* Width tier per route (pageWidth.ts): wide data screens grow with ultrawide windows, reading pages stay at 1440 px. */}
                <main
                  id="main-content"
                  data-page-width={width}
                  className={cn("mx-auto w-full p-6", MAIN_WIDTH_CLASS[width])}
                >
                  <Content>{children}</Content>
                </main>
              </SidebarInset>
            </SidebarProvider>
            <Notification />
          </AiApiRoot>
        </M365ApiRoot>
      </DialerApiRoot>
    </DataImportProvider>
  );
};
